(function() {
    'use strict';

    function resolveFormSubmitButton(form) {
        if (!form) {
            return null;
        }

        const externalButtonId = form.getAttribute('data-external-submit-id');
        if (externalButtonId) {
            return document.getElementById(externalButtonId);
        }

        return form.querySelector('button[type="submit"]');
    }

    function transitionFormSubmitButton(state, applyContentChange) {
        if (!state || !state.submitButton || typeof applyContentChange !== 'function') {
            return Promise.resolve();
        }

        const button = state.submitButton;
        const isExternal = state.form && !state.form.contains(button);
        if (isExternal) {
            return new Promise(function (resolve) {
                window.requestAnimationFrame(function () {
                    applyContentChange(button);
                    resolve();
                });
            });
        }

        button.classList.add('form-submit-button--transitioning');

        return new Promise(function (resolve) {
            window.setTimeout(function () {
                applyContentChange(button);
                window.requestAnimationFrame(function () {
                    button.classList.remove('form-submit-button--transitioning');
                    resolve();
                });
            }, 140);
        });
    }

    function getFormAsyncState(form) {
        if (!form) {
            return null;
        }

        const stateContainer = form.querySelector('.form-async-state');
        const submitButton = resolveFormSubmitButton(form);
        if (!stateContainer || !submitButton) {
            return null;
        }

        if (!submitButton.dataset.formSubmitOriginalHtml) {
            submitButton.dataset.formSubmitOriginalHtml = submitButton.innerHTML;
            submitButton.dataset.formSubmitOriginalClass = submitButton.className;
            submitButton.classList.add('form-submit-managed');
        }

        return {
            form: form,
            submitButton: submitButton,
            stateContainer: stateContainer,
            errorDiv: stateContainer.querySelector('.form-error'),
            errorMessage: stateContainer.querySelector('.error-message'),
            successMessage: stateContainer.querySelector('.form-success-message'),
            loadingText: stateContainer.dataset.loadingText || 'Отправка...',
            successText: stateContainer.dataset.successText || 'Отправлено',
            successResetMs: Number.parseInt(stateContainer.dataset.successResetMs || '6000', 10)
        };
    }

    function resetFormSubmitButton(state) {
        if (!state || !state.submitButton) {
            return;
        }

        const { submitButton } = state;
        if (submitButton.dataset.formSubmitOriginalClass) {
            submitButton.className = submitButton.dataset.formSubmitOriginalClass;
        }

        if (submitButton.dataset.formSubmitOriginalHtml) {
            submitButton.innerHTML = submitButton.dataset.formSubmitOriginalHtml;
        }

        submitButton.disabled = false;
        submitButton.removeAttribute('disabled');
        submitButton.removeAttribute('aria-busy');
        submitButton.removeAttribute('aria-disabled');
        submitButton.dataset.formSubmitState = 'idle';
    }

    function hideFormError(state) {
        if (!state || !state.errorDiv) {
            return;
        }

        state.errorDiv.classList.add('form-error--hidden');
    }

    function setFormSubmitLoading(form) {
        const state = getFormAsyncState(form);
        if (!state) {
            return Promise.resolve(null);
        }

        if (form.__formSubmitSuccessTimer) {
            clearTimeout(form.__formSubmitSuccessTimer);
            form.__formSubmitSuccessTimer = null;
        }

        hideFormError(state);
        resetFormSubmitButton(state);

        return transitionFormSubmitButton(state, function (button) {
            button.disabled = true;
            button.classList.add('btn-loading');
            button.setAttribute('aria-busy', 'true');
            button.dataset.formSubmitState = 'loading';
            button.innerHTML = `<span class="form-submit-spinner" aria-hidden="true"></span><span class="form-submit-button__label">${state.loadingText}</span>`;
            form.__formSubmitLoadingStartedAt = Date.now();
        }).then(function () {
            return state;
        });
    }

    function setFormSubmitSuccess(form) {
        const state = getFormAsyncState(form);
        if (!state) {
            return Promise.resolve();
        }

        hideFormError(state);

        return transitionFormSubmitButton(state, function (button) {
            button.disabled = false;
            button.removeAttribute('disabled');
            button.removeAttribute('aria-busy');
            button.classList.remove('btn-primary', 'btn-outline-primary', 'btn-secondary', 'btn-outline-secondary', 'btn-danger', 'btn-loading');
            button.classList.add('btn-success', 'form-submit-button--success');
            button.dataset.formSubmitState = 'success';
            button.setAttribute('aria-disabled', 'true');
            button.innerHTML = `<i class="fas fa-check" aria-hidden="true"></i><span class="ms-2">${state.successText}</span>`;

            if (state.successMessage) {
                state.successMessage.classList.remove('visually-hidden');
                state.successMessage.classList.add('form-success-message--visible');
            }
        }).then(function () {
            if (Number.isFinite(state.successResetMs) && state.successResetMs > 0) {
                form.__formSubmitSuccessTimer = window.setTimeout(function () {
                    resetFormSubmitState(form);
                }, state.successResetMs);
            }
        });
    }

    function setFormSubmitError(form, message) {
        const state = getFormAsyncState(form);
        if (!state) {
            return Promise.resolve();
        }

        return transitionFormSubmitButton(state, function () {
            resetFormSubmitButton(state);

            if (state.errorMessage && message) {
                state.errorMessage.textContent = message;
            }

            if (state.errorDiv) {
                state.errorDiv.classList.remove('form-error--hidden');
            }
        });
    }

    function resetFormSubmitState(form) {
        const state = getFormAsyncState(form);
        if (!state) {
            return;
        }

        if (form.__formSubmitSuccessTimer) {
            clearTimeout(form.__formSubmitSuccessTimer);
            form.__formSubmitSuccessTimer = null;
        }

        hideFormError(state);
        resetFormSubmitButton(state);

        if (state.successMessage) {
            state.successMessage.classList.add('visually-hidden');
            state.successMessage.classList.remove('form-success-message--visible');
        }
    }

    function waitForMinimumLoadingDuration(form, minimumDurationMs) {
        const startedAt = form && typeof form.__formSubmitLoadingStartedAt === 'number'
            ? form.__formSubmitLoadingStartedAt
            : Date.now();
        const minimumDuration = Number.isFinite(minimumDurationMs) ? minimumDurationMs : 4000;
        const elapsed = Date.now() - startedAt;
        const remaining = Math.max(0, minimumDuration - elapsed);

        if (remaining === 0) {
            return Promise.resolve();
        }

        return new Promise(function (resolve) {
            window.setTimeout(resolve, remaining);
        });
    }

    if (typeof window !== 'undefined') {
        window.FormSubmitState = {
            getState: getFormAsyncState,
            setLoading: setFormSubmitLoading,
            setSuccess: setFormSubmitSuccess,
            setError: setFormSubmitError,
            reset: resetFormSubmitState,
            waitForMinimumLoadingDuration: waitForMinimumLoadingDuration
        };
    }
})();
